/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  numericPixels,
  numericValues,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { Skeleton } from "../../_core/Skeleton/Skeleton.emotion";
import { Text } from "../../_core/Text/Text.emotion";

const containerStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  background: theme.semanticColors.background,
  border: `2px solid ${theme.semanticColors.neutralsLowest}`,
  borderRadius: radius.m,
  display: "flex",
});

const containerVerticalStyle: Interpolation = {
  flexDirection: "column",
  gap: spacing.xxs,
  justifyContent: "center",
  padding: spacing.l,
  textAlign: "center",
};
const valueTextVerticalStyle: Interpolation<ThemeType> = (theme) => ({
  alignSelf: "stretch",
  fontFamily: theme.fontFamilies.sansSerif,
  fontSize: numericPixels[32],
  fontWeight: numericValues[400],
});
const labelTextVerticalStyle: Interpolation<ThemeType> = (theme) => ({
  alignSelf: "stretch",
  color: theme.semanticColors.neutralsHigh,
});
const skeletonVerticalStyle: Interpolation = {
  height: "106px",
};

const containerHorizontalStyle: Interpolation = {
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: spacing.ms,
  width: "100%",
};
const labelTextHorizontalStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  textAlign: "left",
});
const valueTextHorizontalStyle: Interpolation = {
  marginLeft: spacing.xs,
  textAlign: "right",
};
const skeletonHorizontalStyle: Interpolation = {
  height: "42px",
};

export type StatisticCardProps = {
  value: string;
  label: string;
  loading?: boolean;
  className?: string;
  variant?: "vertical" | "horizontal";
};

export const StatisticCard = ({
  value,
  label,
  loading,
  variant = "vertical",
  className = "",
}: StatisticCardProps) => {
  if (loading) {
    return (
      <Skeleton
        css={
          variant === "vertical"
            ? skeletonVerticalStyle
            : skeletonHorizontalStyle
        }
      />
    );
  }

  if (variant === "horizontal") {
    return (
      <div
        css={[containerStyle, containerHorizontalStyle]}
        className={className}
      >
        <Text
          overflow="ellipsis"
          type="title"
          size="xs"
          css={labelTextHorizontalStyle}
        >
          {label}
        </Text>
        <Text type="detail" size="xl" css={valueTextHorizontalStyle}>
          {value}
        </Text>
      </div>
    );
  }

  return (
    <div css={[containerStyle, containerVerticalStyle]} className={className}>
      <span css={valueTextVerticalStyle}>{value}</span>
      <Text
        overflow="ellipsis"
        type="detail"
        size="l"
        css={labelTextVerticalStyle}
      >
        {label}
      </Text>
    </div>
  );
};

StatisticCard.displayName = "StatisticCard";
